/**
 * DriverDeliveriesPage — `/driver-portal/deliveries`.
 *
 * Live · `GET /v1/pods/queue` filtered client-side by the signed-in driver.
 * The queue is shared with operations review (Phase 3a wired the same
 * endpoint at `/delivery/pod`) — the driver portal scopes the rows down to
 * "my own captures" and removes the approve/reject controls.
 */
import { type JSX, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  FileDown,
  MapPin,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { downloadPodPdf } from '../../lib/api/delivery';
import { listMyPodHistory } from '../../lib/api/driver-portal';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';
import { useDriverPortalContext } from './DriverPortalLayout';

async function handleDownloadPod(podId: string): Promise<void> {
  const { blob, filename } = await downloadPodPdf(podId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type StatusFilter = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';

export function DriverDeliveriesPage(): JSX.Element {
  const { driverId } = useDriverPortalContext();
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<StatusFilter>('ALL');

  const queue = useQuery({
    queryKey: ['driver-portal', 'pods'],
    queryFn: () => listMyPodHistory(),
    retry: false,
    staleTime: 30_000,
  });

  const denied =
    queue.isError &&
    queue.error instanceof ApiError &&
    (queue.error.status === 401 || queue.error.status === 403);

  const filtered = useMemo(() => {
    return (queue.data?.items ?? [])
      .filter((p) => p.driverId === driverId)
      .filter((p) => {
        if (filter !== 'ALL' && p.reviewStatus !== filter) return false;
        if (search.trim().length === 0) return true;
        const needle = search.toLowerCase();
        return (
          p.recipientName.toLowerCase().includes(needle) ||
          p.mailItemId.toLowerCase().includes(needle)
        );
      });
  }, [queue.data, driverId, search, filter]);

  return (
    <div>
      <PageHeader
        title="Delivery history"
        description="Past 30 days. Click any row to download proof of delivery."
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Tracking number, recipient or area"
      >
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 text-sm border border-border rounded-lg bg-white"
          aria-label="Status filter"
        >
          <option value="ALL">All</option>
          <option value="APPROVED">Delivered</option>
          <option value="PENDING">Pending review</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </FilterBar>

      {queue.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry{' '}
          <code className="font-mono">Delivery/readPod</code>.
        </DeferredNotice>
      ) : queue.isError ? (
        <DeferredNotice title="Service unreachable">
          {(queue.error as Error).message}
        </DeferredNotice>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Tracking
                </th>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Recipient
                </th>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Status
                </th>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Geo
                </th>
                <th className="text-right px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Captured
                </th>
                <th className="text-right px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  POD
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-900">
                    {p.mailItemId}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">
                      {p.recipientName}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={p.reviewStatus} />
                  </td>
                  <td className="px-4 py-3 text-xs text-muted">
                    {p.latitude && p.longitude ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {p.latitude}, {p.longitude}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-muted">
                    {new Date(p.capturedAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        void handleDownloadPod(p.id);
                      }}
                      data-testid={`download-pod-${p.id}`}
                      className="text-xs inline-flex items-center gap-1 text-nipost-green hover:underline"
                    >
                      <FileDown className="w-3 h-3" /> PDF
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted text-sm"
                  >
                    {(queue.data?.items.length ?? 0) === 0
                      ? 'No deliveries found.'
                      : 'No deliveries match the filter.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Captured" value={filtered.length} />
        <Stat
          label="Approved"
          value={filtered.filter((p) => p.reviewStatus === 'APPROVED').length}
          icon={<CheckCircle2 className="w-3 h-3 text-success" />}
        />
        <Stat
          label="Pending"
          value={filtered.filter((p) => p.reviewStatus === 'PENDING').length}
        />
        <Stat
          label="Rejected"
          value={filtered.filter((p) => p.reviewStatus === 'REJECTED').length}
          icon={<AlertCircle className="w-3 h-3 text-warning" />}
        />
      </div>
    </div>
  );
}

function Stat(props: {
  readonly label: string;
  readonly value: number;
  readonly icon?: JSX.Element;
}): JSX.Element {
  return (
    <div className="bg-white border border-border rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-widest text-muted font-semibold inline-flex items-center gap-1">
        {props.icon} {props.label}
      </p>
      <p className="text-xl font-extrabold text-gray-900 mt-0.5">{props.value}</p>
    </div>
  );
}
